import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { usersAPI, clientsAPI } from '../../api/client';
import UserAvatar from '../../components/UserAvatar';
import ChangeStatusModal, { USER_STATUS_CONFIG, UserStatusBadge, getUserStatus } from '../../components/ChangeStatusModal';
import { Plus, Search, X, Edit2, Eye, Key, ChevronDown, Clock, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import apiClient from '../../api/client';
import ArchiveButton from '../../components/ArchiveButton';

const ROLE_CONFIG = {
  super_admin:    { label: 'Super Admin',      bg: 'bg-purple-100 text-purple-800' },
  lider_proyecto: { label: 'Líder',            bg: 'bg-blue-100 text-blue-800'    },
  levantador:     { label: 'Levantador',       bg: 'bg-green-100 text-green-800'  },
  cliente:        { label: 'Cliente',          bg: 'bg-orange-100 text-orange-800'},
};

function timeAgo(dateStr) {
  if (!dateStr) return '—';
  const seconds = Math.floor((new Date() - new Date(dateStr)) / 1000);
  if (seconds < 60) return 'Ahora';
  if (seconds < 3600) return `hace ${Math.floor(seconds / 60)} min`;
  if (seconds < 86400) return `hace ${Math.floor(seconds / 3600)} h`;
  if (seconds < 2592000) return `hace ${Math.floor(seconds / 86400)} días`;
  return new Date(dateStr).toLocaleDateString('es-MX');
}

function ActionMenu({ user, onEdit, onView, onStatusChange, onResetPwd, onArchived, isLast }) {
  const [open, setOpen] = useState(false);
  const dropdownCls = isLast
    ? 'absolute right-0 bottom-full mb-1 w-48 bg-white border border-slate-200 rounded-sm shadow-xl z-40 text-sm'
    : 'absolute right-0 top-full mt-1 w-48 bg-white border border-slate-200 rounded-sm shadow-xl z-40 text-sm';
  return (
    <div className="relative">
      <button onClick={() => setOpen(!open)}
        className="flex items-center gap-1 text-xs text-slate-400 hover:text-slate-600 px-2 py-1 border border-slate-200 rounded-sm hover:bg-slate-50">
        Acciones <ChevronDown size={12} />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
          <div className={dropdownCls}>
            <button onClick={() => { setOpen(false); onView(); }}
              className="w-full text-left flex items-center gap-2 px-3 py-2 hover:bg-blue-50 text-soem-navy">
              <Eye size={13} /> Ver perfil
            </button>
            <button onClick={() => { setOpen(false); onEdit(); }}
              className="w-full text-left flex items-center gap-2 px-3 py-2 hover:bg-blue-50 text-soem-navy">
              <Edit2 size={13} /> Editar
            </button>
            <div className="border-t border-slate-100" />
            <button onClick={() => { setOpen(false); onStatusChange(); }}
              className="w-full text-left flex items-center gap-2 px-3 py-2 hover:bg-blue-50 text-soem-navy">
              <Clock size={13} /> Cambiar estado
            </button>
            <button onClick={() => { setOpen(false); onResetPwd(); }}
              className="w-full text-left flex items-center gap-2 px-3 py-2 hover:bg-orange-50 text-orange-700">
              <Key size={13} /> Restablecer contraseña
            </button>
            <div className="border-t border-slate-100 px-3 py-2">
              <ArchiveButton entity="users" id={user.id} name={user.name || user.email} onArchived={onArchived} className="show-label w-full text-left" />
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default function UsersPage() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [statusModal, setStatusModal] = useState(null);
  const [filters, setFilters] = useState({ q: '', role: '', status: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // #M4 — la búsqueda/filtro es client-side sobre lo cargado (abajo),
      // pero el fetch pedía solo 100 mientras el header muestra `total`
      // real del backend. Con más de 100 usuarios, buscar a alguien más
      // allá del corte devolvía "No se encontraron usuarios" pese a que
      // sí existía. Se sube el límite para que coincida con el total.
      const params = { limit: 1000 };
      if (filters.role) params.role = filters.role;
      const r = await usersAPI.list(params);
      setUsers((r.data.items || []).filter(i => !i.is_archived));
      setTotal(r.data.total || 0);
    } catch { toast.error('Error al cargar usuarios'); }
    finally { setLoading(false); }
  }, [filters.role]);

  useEffect(() => { load(); }, [load]);

  const handleResetPwd = async (user) => {
    try {
      await apiClient.post('/auth/forgot-password', { email: user.email });
      toast.success(`Instrucciones de reseteo enviadas a ${user.email}`);
    } catch { toast.error('Error al enviar email'); }
  };

  const filtered = users.filter(u => {
    const status = getUserStatus(u);
    if (filters.status && status !== filters.status) return false;
    if (filters.q) {
      const q = filters.q.toLowerCase();
      return u.name?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q) || (u.phone || '').includes(q);
    }
    return true;
  });

  // Expiring temporal alert
  const expiringToday = users.filter(u => {
    if (getUserStatus(u) !== 'temporal' || !u.temporary_access_expires_at) return false;
    const diff = (new Date(u.temporary_access_expires_at) - new Date()) / 3600000;
    return diff > 0 && diff <= 24;
  });

  const stats = {
    total: users.length,
    activos: users.filter(u => getUserStatus(u) === 'activo').length,
    inactivos: users.filter(u => getUserStatus(u) === 'inactivo').length,
    suspendidos: users.filter(u => getUserStatus(u) === 'suspendido').length,
    temporales: users.filter(u => getUserStatus(u) === 'temporal').length,
  };

  const STATUS_TOOLTIPS = {
    total:      'Todos los usuarios registrados en el sistema.',
    activos:    'Usuarios con acceso completo y vigente.',
    inactivos:  'Usuarios con acceso restringido, pero aún registrados (sin baja).',
    suspendidos:'Usuarios con funciones limitadas temporalmente.',
    temporales: 'Usuarios con acceso de "solo lectura" por tiempo limitado.',
  };

  const hasFilters = filters.q || filters.role || filters.status;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="users-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-soem-navy">Gestión de Usuarios</h1>
          <p className="text-slate-500 text-sm mt-1">{total} usuarios en el sistema</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => navigate('/dashboard/usuarios/organigrama')}
            className="soem-btn-secondary px-4 py-2 text-sm" data-testid="org-chart-btn">
            Organigrama
          </button>
          <button onClick={() => navigate('/dashboard/usuarios/nuevo')}
            id="btn-nuevo-usuario"
            className="soem-btn-primary px-4 py-2 text-sm flex items-center gap-2" data-testid="create-user-btn">
            <Plus size={16} /> Nuevo Usuario
          </button>
        </div>
      </div>

      {/* Stats con tooltips */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {[
          { key: 'total',      label: 'Total',       value: stats.total,       color: '#1A56DB' },
          { key: 'activos',    label: 'Activos',     value: stats.activos,     color: '#15803D' },
          { key: 'inactivos',  label: 'Inactivos',   value: stats.inactivos,   color: '#94a3b8' },
          { key: 'suspendidos',label: 'Suspendidos', value: stats.suspendidos, color: '#DC2626' },
          { key: 'temporales', label: 'Temporales',  value: stats.temporales,  color: '#D97706' },
        ].map(s => (
          <div key={s.key} className="soem-card p-4 border-t-4 group relative cursor-help"
            style={{ borderTopColor: s.color }} title={STATUS_TOOLTIPS[s.key]}>
            <p className="soem-label">{s.label}</p>
            <p className="text-3xl font-black text-soem-navy">{s.value}</p>
            {/* Tooltip */}
            <div className="absolute bottom-full left-0 mb-2 w-52 bg-soem-navy text-white text-xs rounded-sm p-2.5 shadow-xl opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
              {STATUS_TOOLTIPS[s.key]}
              <div className="absolute top-full left-4 w-2 h-2 bg-soem-navy rotate-45 -mt-1" />
            </div>
          </div>
        ))}
      </div>

      {/* Expiring alert */}
      {expiringToday.map(u => (
        <div key={u.id} className="bg-orange-50 border border-orange-200 rounded-sm p-3 flex items-center gap-2">
          <AlertTriangle size={16} className="text-orange-600" />
          <span className="text-orange-800 text-sm font-medium">
            Acceso temporal próximo a vencer: <strong>{u.name}</strong> — vence{' '}
            {new Date(u.temporary_access_expires_at).toLocaleTimeString('es-MX')}
          </span>
        </div>
      ))}

      {/* Filters */}
      <div className="soem-card p-4 flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-48">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input className="pl-9 pr-4 py-2 border border-slate-300 rounded-sm text-sm w-full focus:outline-none focus:ring-2 focus:ring-soem-primary"
            placeholder="Nombre, email o teléfono..." aria-label="Buscar usuario" value={filters.q}
            onChange={e => setFilters(f => ({ ...f, q: e.target.value }))} data-testid="user-search" />
        </div>
        <select className="px-3 py-2 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-soem-primary"
          value={filters.role} aria-label="Filtrar por rol" onChange={e => setFilters(f => ({ ...f, role: e.target.value }))}>
          <option value="">Todos los roles</option>
          {Object.entries(ROLE_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <select className="px-3 py-2 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-soem-primary"
          value={filters.status} aria-label="Filtrar por estado" onChange={e => setFilters(f => ({ ...f, status: e.target.value }))}>
          <option value="">Todos los estados</option>
          {Object.entries(USER_STATUS_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        {hasFilters && (
          <button onClick={() => setFilters({ q: '', role: '', status: '' })}
            className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-soem-primary">
            <X size={14} /> Limpiar
          </button>
        )}
      </div>

      {/* Table */}
      <div className="soem-card overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="soem-table-header">
              <th className="px-5 py-3 text-left">Usuario</th>
              <th className="px-5 py-3 text-left">Rol</th>
              <th className="px-5 py-3 text-left">Teléfono</th>
              <th className="px-5 py-3 text-left">Estado</th>
              <th className="px-5 py-3 text-left">Jefe directo</th>
              <th className="px-5 py-3 text-left">Último acceso</th>
              <th className="px-5 py-3 text-left">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              [...Array(5)].map((_, i) => (
                <tr key={i}><td colSpan={7} className="px-5 py-3"><div className="h-8 bg-slate-100 rounded animate-pulse" /></td></tr>
              ))
            ) : filtered.length === 0 ? (
              <tr><td colSpan={7} className="text-center text-slate-400 py-10">No se encontraron usuarios.</td></tr>
            ) : (
              filtered.map((u, idx) => {
                const roleCfg = ROLE_CONFIG[u.role] || { label: u.role, bg: 'bg-slate-100 text-slate-600' };
                return (
                  <tr key={u.id} className="soem-table-row" data-testid={`user-row-${u.id}`}>
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-3">
                        <UserAvatar user={u} size="sm" />
                        <div>
                          <p className="font-semibold text-soem-navy">{u.full_name || u.name}</p>
                          <p className="text-slate-400 text-xs">{u.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-3">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${roleCfg.bg}`}>{roleCfg.label}</span>
                    </td>
                    <td className="px-5 py-3 text-slate-500">{u.phone || '—'}</td>
                    <td className="px-5 py-3">
                      <UserStatusBadge user={u} />
                    </td>
                    <td className="px-5 py-3 text-slate-500 text-xs">{u.direct_supervisor_name || '—'}</td>
                    <td className="px-5 py-3 text-slate-400 text-xs">{timeAgo(u.last_login_at)}</td>
                    <td className="px-5 py-3">
                      <ActionMenu user={u}
                        isLast={idx >= filtered.length - 3}
                        onView={() => navigate(`/dashboard/usuarios/${u.id}`)}
                        onEdit={() => navigate(`/dashboard/usuarios/${u.id}/editar`)}
                        onStatusChange={() => setStatusModal(u)}
                        onResetPwd={() => handleResetPwd(u)}
                        onArchived={load}
                      />
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {statusModal && (
        <ChangeStatusModal user={statusModal}
          onChanged={load} onClose={() => setStatusModal(null)} />
      )}
    </div>
  );
}
